import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { auth as authApi } from '../utils/api';
import AvvisoIscrizioniChiuse from '../components/shared/AvvisoIscrizioniChiuse';
import { CODICE_ISCRIZIONI_CHIUSE, MSG_ISCRIZIONI_CHIUSE } from './iscrizioniChiuse';

const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// Cantiere Registrazione, Fetta 2b (DEC-064).
// Form di adesione self-service. Ci si arriva dal login SSO quando l'email non
// e' ancora registrata e la registrazione e' aperta: OAuthCallback mette in
// sessione il biglietto firmato + i dati identita' (solo per mostrarli) e
// naviga qui. L'identita' autorevole resta il biglietto lato server (R62): i
// campi email/nome in sessione servono solo a scrivere a schermo "stai
// registrando <email>".
//
// Mappa dei codici d'errore del backend (views_registration.py) in messaggi per
// l'artigiano — mai codici o stack trace a video (Regola 14).
const MSG_ERRORE = {
  campi_obbligatori_mancanti: 'Compila tutti i campi obbligatori.',
  piva_non_valida: 'La Partita IVA non è valida. Controlla le 11 cifre.',
  telefono_non_valido: 'Il numero di telefono non è valido.',
  email_fatturazione_non_valida: 'L\'email per le fatture non è valida.',
  piva_gia_registrata: 'Questa Partita IVA è già registrata su mAIstro.',
  telefono_gia_registrato: 'Questo numero di telefono è già associato a un account.',
  account_gia_registrato: 'Questo account è già registrato. Accedi con Google o Microsoft.',
  // Fetta 4 — anti-abuso trial (DEC-057)
  trial_gia_usato: 'Risulta che hai già usato la prova gratuita di mAIstro. Per continuare scegli un piano, oppure scrivici a info@appmaistro.it se pensi sia un errore.',
  registrazione_chiusa: MSG_ISCRIZIONI_CHIUSE,
  creazione_fallita: 'Non siamo riusciti a creare l\'account. Riprova o scrivi a info@appmaistro.it.',
  // Fetta 3 — verifica telefono via SMS (DEC-056)
  otp_mancante: 'Inserisci il codice che ti abbiamo inviato via SMS.',
  otp_non_valido: 'Codice non valido o scaduto. Richiedine uno nuovo.',
  otp_cooldown: 'Hai già richiesto un codice da poco. Attendi un minuto prima di riprovare.',
  sms_non_configurato: 'Il servizio di invio SMS non è al momento disponibile. Riprova più tardi.',
  sms_non_inviato: 'Non siamo riusciti a inviare l\'SMS a questo numero. Controlla che sia corretto e riprova.',
};
// Codici del biglietto: la sessione di registrazione non è più valida →
// l'unica via d'uscita è rifare l'accesso.
const CODICI_BIGLIETTO = new Set([
  'biglietto_mancante', 'biglietto_scaduto', 'biglietto_non_valido', 'biglietto_senza_email',
]);

const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--ds-text)', display: 'block', marginBottom: 6 };
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)',
  background: 'var(--chip)', fontSize: 12, color: 'var(--ds-text)', boxSizing: 'border-box',
};

export default function Registrati() {
  const { completaRegistrazione } = useAuth();
  const navigate = useNavigate();

  // Biglietto + prefill dalla sessione (messi da OAuthCallback).
  const [ticket] = useState(() => sessionStorage.getItem('registration_ticket') || '');
  const [prefill] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('registration_prefill') || '{}'); }
    catch { return {}; }
  });

  const [form, setForm] = useState({
    ragione_sociale: '', piva: '', telefono: '', citta: '', provincia: '',
    indirizzo: '', settore: '', codice_ateco: '',
  });
  const [usaEmailAccesso, setUsaEmailAccesso] = useState(true);
  const [emailFatturazione, setEmailFatturazione] = useState('');
  const [mostraAvanzati, setMostraAvanzati] = useState(false);
  const [error, setError] = useState('');
  const [bigliettoScaduto, setBigliettoScaduto] = useState(false);
  const [chiuse, setChiuse] = useState(false);  // DBT-228
  const [loading, setLoading] = useState(false);
  // Fetta 3 — verifica telefono via SMS (DEC-056). Il flusso è a due tempi in
  // una pagina sola: prima si invia il codice, poi lo si conferma creando.
  const [otpInviato, setOtpInviato] = useState(false);
  const [otpCode, setOtpCode] = useState('');

  const set = (campo) => (e) => {
    const val = campo === 'provincia' ? e.target.value.toUpperCase() : e.target.value;
    setForm((f) => ({ ...f, [campo]: val }));
  };

  // Controllo minimo lato client: presenza dei campi obbligatori (la forma —
  // checksum P.IVA, telefono E.164 — la valida il backend, unica verità).
  const obbligatoriMancanti = () => {
    const obbligatori = ['ragione_sociale', 'piva', 'telefono', 'citta', 'provincia'];
    return obbligatori.some((k) => !form[k].trim());
  };

  // Gestione uniforme degli errori: biglietto scaduto → torna al login;
  // altrimenti messaggio in italiano dalla mappa.
  const gestisciErrore = (err, fallback) => {
    const codice = err.response?.data?.codice;
    if (codice === CODICE_ISCRIZIONI_CHIUSE) {
      setChiuse(true);
      return;
    }
    if (codice && CODICI_BIGLIETTO.has(codice)) {
      sessionStorage.removeItem('registration_ticket');
      sessionStorage.removeItem('registration_prefill');
      setBigliettoScaduto(true);
      return;
    }
    setError(MSG_ERRORE[codice] || err.response?.data?.error || fallback);
  };

  // Tempo 1: invia il codice SMS al numero del form.
  const inviaOtp = async () => {
    setError('');
    if (obbligatoriMancanti()) {
      setError('Compila tutti i campi obbligatori.');
      return;
    }
    setLoading(true);
    try {
      await authApi.registerSendOtp({
        registration_ticket: ticket,
        telefono: form.telefono.trim(),
      });
      setOtpInviato(true);
    } catch (err) {
      gestisciErrore(err, 'Non siamo riusciti a inviare il codice. Riprova tra qualche istante.');
    } finally {
      setLoading(false);
    }
  };

  // Tempo 2: col codice, crea l'account e fai entrare.
  const creaAccount = async () => {
    setError('');
    if (!otpCode.trim()) {
      setError('Inserisci il codice che ti abbiamo inviato via SMS.');
      return;
    }
    const payload = {
      registration_ticket: ticket,
      otp_code: otpCode.trim(),
      ragione_sociale: form.ragione_sociale.trim(),
      piva: form.piva.trim(),
      telefono: form.telefono.trim(),
      citta: form.citta.trim(),
      provincia: form.provincia.trim(),
      indirizzo: form.indirizzo.trim(),
      settore: form.settore.trim(),
      codice_ateco: form.codice_ateco.trim(),
    };
    // Email fatture: campo separato solo se l'artigiano ne vuole una diversa
    // (DEC-065). Spunta attiva o campo vuoto → il backend usa l'email d'accesso.
    if (!usaEmailAccesso && emailFatturazione.trim()) {
      payload.email_fatturazione = emailFatturazione.trim();
    }
    setLoading(true);
    try {
      await completaRegistrazione(payload);
      // Pulizia: il biglietto è monouso, non deve restare in sessione.
      sessionStorage.removeItem('registration_ticket');
      sessionStorage.removeItem('registration_prefill');
      navigate('/');
    } catch (err) {
      gestisciErrore(err, 'Registrazione non riuscita. Riprova tra qualche istante.');
    } finally {
      setLoading(false);
    }
  };

  // Il bottone primario cambia mestiere: prima invia il codice, poi crea.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!otpInviato) inviaOtp();
    else creaAccount();
  };

  // «Cambia numero»: torna a modificare il telefono, azzerando il codice.
  const cambiaNumero = () => {
    setOtpInviato(false);
    setOtpCode('');
    setError('');
  };

  // Pulisce eventuale biglietto residuo tornando al login.
  const tornaAlLogin = () => {
    sessionStorage.removeItem('registration_ticket');
    sessionStorage.removeItem('registration_prefill');
    navigate('/auth/login');
  };

  // ── Iscrizioni chiuse (DBT-228): avviso + «Richiedi una demo» ──
  if (chiuse) {
    return (
      <Guscio>
        <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginTop: 0, marginBottom: 16, fontFamily: 'var(--font-display)', textAlign: 'center' }}>
          Iscrizioni in arrivo
        </h2>
        <AvvisoIscrizioniChiuse />
      </Guscio>
    );
  }

  // ── STATO "vuoto": nessun biglietto (accesso diretto o sessione scaduta) ──
  if (!ticket || bigliettoScaduto) {
    return (
      <Guscio>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginTop: 0, marginBottom: 8, fontFamily: 'var(--font-display)' }}>
            {bigliettoScaduto ? 'Sessione scaduta' : 'Registrazione'}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--ds-muted)', lineHeight: 1.6, marginBottom: 20 }}>
            {bigliettoScaduto
              ? 'La sessione di registrazione è scaduta. Rifai l\'accesso con Google o Microsoft e completa il form entro qualche minuto.'
              : 'Per registrarti, accedi con Google o Microsoft: se il tuo account non esiste ancora, ti porteremo qui per completare i dati.'}
          </p>
          <button
            onClick={tornaAlLogin}
            className="gradient-brand"
            style={{
              width: '100%', padding: '11px 0', borderRadius: 'var(--radius-base)', border: 'none',
              cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'white',
            }}
          >
            Vai all'accesso
          </button>
        </div>
      </Guscio>
    );
  }

  // ── FORM ──
  return (
    <Guscio>
      <h2 style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginBottom: 6, marginTop: 0, fontFamily: 'var(--font-display)' }}>
        Completa la registrazione
      </h2>
      <p style={{ fontSize: 12, color: 'var(--ds-muted)', marginBottom: 20, marginTop: 0 }}>
        {prefill.email
          ? <>Stai registrando <strong>{prefill.email}</strong>. Ancora qualche dato e inizi la prova gratuita.</>
          : 'Ancora qualche dato sulla tua attività e inizi la prova gratuita.'}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Ragione sociale *</label>
          <input value={form.ragione_sociale} onChange={set('ragione_sociale')} placeholder="Es. Edilizia Rossi SRL" required style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>Partita IVA *</label>
          <input value={form.piva} onChange={set('piva')} placeholder="11 cifre" inputMode="numeric" required style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>Telefono *</label>
          <input
            value={form.telefono} onChange={set('telefono')}
            placeholder="+39 333 1234567" inputMode="tel" required
            disabled={otpInviato}
            style={{ ...inputStyle, ...(otpInviato ? { opacity: 0.6 } : {}) }}
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>Città *</label>
            <input value={form.citta} onChange={set('citta')} placeholder="Milano" required style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Prov. *</label>
            <input value={form.provincia} onChange={set('provincia')} placeholder="MI" maxLength={2} required style={{ ...inputStyle, textTransform: 'uppercase' }} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Indirizzo</label>
          <input value={form.indirizzo} onChange={set('indirizzo')} placeholder="Via Roma 1 (facoltativo)" style={inputStyle} />
        </div>

        {/* Email fatture: default = email d'accesso (DEC-065). */}
        <div style={{
          background: 'var(--chip)', border: '1px solid var(--ds-border)',
          borderRadius: 'var(--radius-base)', padding: '10px 12px',
        }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={usaEmailAccesso}
              onChange={(e) => setUsaEmailAccesso(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }}
            />
            <span style={{ fontSize: 12, color: 'var(--ds-text)', lineHeight: 1.5 }}>
              Usa la mia email di accesso anche per le fatture
              {prefill.email && (
                <span style={{ color: 'var(--ds-muted)' }}> ({prefill.email})</span>
              )}
            </span>
          </label>
          {!usaEmailAccesso && (
            <div style={{ marginTop: 10 }}>
              <label style={labelStyle}>Indirizzo email per le fatture</label>
              <input
                type="email"
                value={emailFatturazione}
                onChange={(e) => setEmailFatturazione(e.target.value)}
                placeholder="amministrazione@tuaazienda.it"
                style={inputStyle}
              />
            </div>
          )}
          {/* Rassicurazione: nella prova gratuita non c'è addebito né fattura
              (DEC-059 «Free = la prova»); la fattura arriva solo con un piano a
              pagamento (DEC-065). Vale anche a spunta attiva. */}
          <p style={{ fontSize: 11, color: 'var(--ds-muted)', lineHeight: 1.5, margin: '10px 0 0' }}>
            Durante la prova gratuita non emettiamo fatture. Riceverai la prima
            fattura a questo indirizzo solo se sceglierai un piano a pagamento.
          </p>
        </div>

        {/* Dati avanzati facoltativi: nascosti per non appesantire l'iscrizione. */}
        <div>
          <button
            type="button"
            onClick={() => setMostraAvanzati((v) => !v)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, fontWeight: 500, color: 'var(--brand)',
            }}
          >
            {mostraAvanzati ? '− Nascondi altri dati' : '+ Altri dati (facoltativi)'}
          </button>
          {mostraAvanzati && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
              <div>
                <label style={labelStyle}>Settore di attività</label>
                <input value={form.settore} onChange={set('settore')} placeholder="Es. Ristrutturazioni" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Codice ATECO</label>
                <input value={form.codice_ateco} onChange={set('codice_ateco')} placeholder="Es. 43.99.09" style={inputStyle} />
              </div>
            </div>
          )}
        </div>

        {/* Verifica telefono via SMS (Fetta 3, DEC-056): compare dopo l'invio
            del codice. Il numero sopra è bloccato finché non si «cambia numero». */}
        {otpInviato && (
          <div style={{
            background: 'var(--chip)', border: '1px solid var(--ds-border)',
            borderRadius: 'var(--radius-base)', padding: '10px 12px',
          }}>
            <p style={{ fontSize: 12, color: 'var(--ds-text)', lineHeight: 1.5, margin: '0 0 10px' }}>
              Ti abbiamo inviato un codice via SMS al <strong>{form.telefono.trim()}</strong>.
              Inseriscilo qui sotto per completare.
            </p>
            <label style={labelStyle}>Codice di verifica (6 cifre)</label>
            <input
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="––––––"
              inputMode="numeric"
              autoComplete="one-time-code"
              style={{ ...inputStyle, letterSpacing: '0.3em', textAlign: 'center', fontSize: 16 }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <button
                type="button" onClick={cambiaNumero}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, color: 'var(--ds-muted)' }}
              >
                Cambia numero
              </button>
              <button
                type="button" onClick={inviaOtp} disabled={loading}
                style={{ background: 'none', border: 'none', padding: 0, cursor: loading ? 'default' : 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}
              >
                Invia di nuovo il codice
              </button>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="registrati-error" style={{ fontSize: 11, color: 'var(--ds-danger)', background: tint('var(--ds-danger)', 8), borderRadius: 'var(--radius-base)', padding: '8px 12px' }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="gradient-brand"
          style={{
            width: '100%', padding: '11px 0', borderRadius: 'var(--radius-base)', border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500, color: 'white',
            marginTop: 4, opacity: loading ? 0.7 : 1, boxShadow: '0 2px 10px rgba(35,38,47,0.06)',
          }}
        >
          {loading
            ? (otpInviato ? 'Creazione account in corso…' : 'Invio codice in corso…')
            : (otpInviato ? 'Crea account e inizia la prova' : 'Invia codice SMS')}
        </button>
      </form>

      <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--ds-muted)', marginTop: 16 }}>
        Hai già un account?{' '}
        <button onClick={tornaAlLogin} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 500, color: 'var(--brand)' }}>
          Accedi
        </button>
      </p>
    </Guscio>
  );
}

// Guscio grafico condiviso (stessa veste del Login): sfondo, logo, card.
function Guscio({ children }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--chip)', padding: '32px 0',
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
          <div className="gradient-brand" style={{ width: 52, height: 52, borderRadius: 'var(--radius-card)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, boxShadow: '0 6px 18px rgba(35,38,47,0.08)' }}>
            <span style={{ color: 'white', fontWeight: 500, fontSize: 22, fontStyle: 'italic' }}>m</span>
          </div>
          <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--ink)', fontFamily: 'var(--font-display)' }}>mAIstro</div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 2 }}>Artigiani</div>
        </div>
        <div style={{
          background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)',
          padding: 28, boxShadow: '0 6px 18px rgba(35,38,47,0.08)',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}
